import React, { createContext, useContext, useState, useCallback } from 'react';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { products } from '../data/products';

const StoreContext = createContext();

export function useStore() {
  return useContext(StoreContext);
}

export function StoreProvider({ children }) {
  // State declarations
  const [wishlist, setWishlist] = useLocalStorage('tv_wish', []);
  const [cart, setCart] = useLocalStorage('tv_cart', []);
  const [filters, setFilters] = useState({
    gender: [],
    cat: [],
    price: 9999,
    discount: 0,
    search: "",
  });
  const [sort, setSort] = useState("popular");
  const [visible, setVisible] = useState(12);
  const [toast, setToast] = useState({ show: false, message: "" });
  const [cartOpen, setCartOpen] = useState(false);
  const [quickViewOpen, setQuickViewOpen] = useState(false);
  const [quickViewProduct, setQuickViewProduct] = useState(null);
  const [orders, setOrders] = useLocalStorage('tv_orders', []);
  const [currentOrder, setCurrentOrder] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useLocalStorage('tv_logged_in', false);
  const [userEmail, setUserEmail] = useLocalStorage('tv_user_email', '');
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [pendingCheckout, setPendingCheckout] = useState(null);

  // --- TOAST (must be defined before being used) ---
  const showToast = useCallback((message) => {
    setToast({ show: true, message });
    setTimeout(() => setToast({ show: false, message: "" }), 2000);
  }, []);

  // --- AUTH METHODS (now can use showToast safely) ---
  const requestOtp = useCallback(async (email) => {
    const otp = Math.floor(100000 + Math.random() * 900000).toString();
    setOtpCode(otp);
    console.log(`[DEMO] OTP for ${email}: ${otp}`);
    setOtpSent(true);
    setUserEmail(email);
    showToast(`OTP sent to ${email} (demo: ${otp})`);
    return true;
  }, [showToast]);

  const verifyOtp = useCallback((enteredOtp) => {
    if (enteredOtp === otpCode) {
      setIsLoggedIn(true);
      setOtpSent(false);
      setOtpCode('');
      showToast('Logged in successfully!');
      return true;
    } else {
      showToast('Invalid OTP. Please try again.');
      return false;
    }
  }, [otpCode, setIsLoggedIn, showToast]);

  const logout = useCallback(() => {
    setIsLoggedIn(false);
    setUserEmail('');
    setOtpSent(false);
    setOtpCode('');
    showToast('Logged out');
  }, [setIsLoggedIn, showToast]);

  const requireLogin = useCallback((callback) => {
    if (isLoggedIn) {
      callback();
    } else {
      setPendingCheckout(() => callback);
      setLoginModalOpen(true);
    }
  }, [isLoggedIn]);

  // --- ORDER METHODS ---
  const createOrder = useCallback(async (orderData) => {
    const newOrder = {
      id: Date.now().toString(),
      ...orderData,
      status: 'Confirmed',
      statusHistory: [{ date: new Date().toISOString(), status: 'Order Placed' }],
      createdAt: new Date().toISOString(),
      trackingId: 'TRK' + Math.floor(Math.random() * 1000000),
    };
    setOrders(prev => [newOrder, ...prev]);
    setCurrentOrder(newOrder);
    setCart([]);
    console.log(`📧 Booking confirmation sent to ${userEmail} for order #${newOrder.id}`);
    console.log(`Order details:`, newOrder);
    showToast(`Order placed successfully! Order ID: ${newOrder.id.slice(-6)}`);
    return newOrder;
  }, [setOrders, setCart, userEmail, showToast]);

  // --- CART METHODS ---
  const addToCart = useCallback((product, size = "M", quantity = 1) => {
    setCart(prev => {
      const existing = prev.find(item => item.id === product.id && item.size === size);
      if (existing) {
        return prev.map(item =>
          item.id === product.id && item.size === size
            ? { ...item, qty: item.qty + quantity }
            : item
        );
      }
      return [...prev, { 
        id: product.id, 
        qty: quantity, 
        size, 
        name: product.name, 
        price: product.price, 
        img: product.img, 
        brand: product.brand 
      }];
    });
    showToast("Added to bag");
  }, [setCart, showToast]);

  const removeFromCart = useCallback((id, size) => {
    setCart(prev => prev.filter(item => !(item.id === id && item.size === size)));
  }, [setCart]);

  const updateCartQuantity = useCallback((id, size, delta) => {
    setCart(prev => prev.map(item => {
      if (item.id === id && item.size === size) {
        const newQty = item.qty + delta;
        if (newQty <= 0) return null;
        return { ...item, qty: newQty };
      }
      return item;
    }).filter(Boolean));
  }, [setCart]);

  // --- WISHLIST METHODS ---
  const toggleWishlist = useCallback((id) => {
    setWishlist(prev => {
      if (prev.includes(id)) {
        showToast("Removed from wishlist");
        return prev.filter(i => i !== id);
      } else {
        showToast("Added to wishlist");
        return [...prev, id];
      }
    });
  }, [setWishlist, showToast]);

  // --- FILTERING & SORTING ---
  const getFilteredProducts = useCallback(() => {
    let filtered = [...products];
    
    if (filters.gender.length) {
      filtered = filtered.filter(p => filters.gender.includes(p.gender));
    }
    if (filters.cat.length) {
      filtered = filtered.filter(p => filters.cat.includes(p.cat));
    }
    if (filters.price) {
      filtered = filtered.filter(p => p.price <= filters.price);
    }
    if (filters.discount) {
      filtered = filtered.filter(p => p.off >= filters.discount);
    }
    if (filters.search) {
      filtered = filtered.filter(p => 
        (p.name + p.brand + p.cat).toLowerCase().includes(filters.search.toLowerCase())
      );
    }
    
    if (sort === 'price-low') filtered.sort((a, b) => a.price - b.price);
    if (sort === 'price-high') filtered.sort((a, b) => b.price - a.price);
    if (sort === 'discount') filtered.sort((a, b) => b.off - a.off);
    if (sort === 'new') filtered.sort((a, b) => b.id - a.id);
    
    return filtered;
  }, [filters, sort]);

  const clearFilters = useCallback(() => {
    setFilters({ gender: [], cat: [], price: 9999, discount: 0, search: "" });
  }, []);

  // --- CONTEXT VALUE ---
  const value = {
    wishlist,
    cart,
    filters,
    sort,
    visible,
    toast,
    cartOpen,
    quickViewOpen,
    quickViewProduct,
    orders,
    currentOrder,
    isLoggedIn,
    userEmail,
    loginModalOpen,
    pendingCheckout,
    setFilters,
    setSort,
    setVisible,
    setCartOpen,
    setQuickViewOpen,
    setQuickViewProduct,
    setLoginModalOpen,
    addToCart,
    removeFromCart,
    updateCartQuantity,
    toggleWishlist,
    getFilteredProducts,
    clearFilters,
    showToast,
    requestOtp,
    verifyOtp,
    logout,
    requireLogin,
    createOrder,
    setOrders,
    setCurrentOrder,
  };

  return (
    <StoreContext.Provider value={value}>
      {children}
    </StoreContext.Provider>
  );
}